/**
 * NmethProposalPage
 * 方法論提案申請表（A6.4-FORM-METH-001）
 * 獨立模組，不綁定專案
 * 清單 + 三步驟精靈（Step1:方法論資訊 → Step2:提案者資訊 → Step3:狀態確認與儲存）
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import nmethProposalService, {
  NmethProposalDetail, NmethProposalCreate, NmethProposalUpdate, AttachmentChecks
} from '../services/nmethProposalService';
import { getSystemCodesByType, SystemCode } from '../services/systemCodeService';
import DateInput from '../components/DateInput';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';
import '../styles/DataTable.css';

// 狀態代碼 (system_codes.id, code_etype='A6.4_nmeth_status')
const STATUS_DRAFT = 258;
const STATUS_SUBMITTED = 259;
const STATUS_REVIEW = 260;
const STATUS_APPROVED = 261;
const STATUS_REJECTED = 262;

const STATUS_LABELS: Record<number, { zh: string; en: string; color: string }> = {
  [STATUS_DRAFT]:     { zh: '草稿/需修訂', en: 'Draft/Revision Required', color: '#999' },
  [STATUS_SUBMITTED]: { zh: '已提交',      en: 'Submitted',              color: '#1890ff' },
  [STATUS_REVIEW]:    { zh: '審查中',      en: 'Under Review',           color: '#fa8c16' },
  [STATUS_APPROVED]:  { zh: '已核准',      en: 'Approved',               color: '#52c41a' },
  [STATUS_REJECTED]:  { zh: '已退回',      en: 'Rejected',               color: '#ff4d4f' },
};

const EMPTY_ATTACHMENTS: AttachmentChecks = {
  draft_pdd: false, draft_poa_dd: false, meth_form_002: false,
  additional_info: false, additional_description: '',
};

const WIZARD_STEPS = [
  { zh: 'Step 1. 方法論基本資訊', en: 'Step 1. Methodology Information' },
  { zh: 'Step 2. 提案者資訊', en: 'Step 2. Proponent Details' },
  { zh: 'Step 3. 狀態確認與儲存', en: 'Step 3. Status & Save' },
];

const NmethProposalPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { hasPermission, loading: permissionLoading } = usePermission();
  const toast = useToast();
  const hasInitialized = useRef(false);

  // ========== 資料狀態 ==========
  const [dataList, setDataList] = useState<NmethProposalDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ========== 代碼選項 ==========
  const [standardTypes, setStandardTypes] = useState<SystemCode[]>([]);
  const [mitigationTypes, setMitigationTypes] = useState<SystemCode[]>([]);
  const [sectoralScopes, setSectoralScopes] = useState<SystemCode[]>([]);
  const [statusCodes, setStatusCodes] = useState<SystemCode[]>([]);

  // ========== 模式 ==========
  const [mode, setMode] = useState<'list' | 'wizard'>('list');
  const [editingItem, setEditingItem] = useState<NmethProposalDetail | null>(null);
  const [wizardStep, setWizardStep] = useState(0); // 0, 1, 2

  // ========== 表單資料（精靈完成前不送出） ==========
  const [formData, setFormData] = useState({
    title: '',
    type_of_standard: null as number | null,
    mitigation_type: null as number | null,
    sectoral_scopes: [] as number[],
    project_title: '',
    attachment_checks: { ...EMPTY_ATTACHMENTS },
    proponent_name: '',
    affiliation: 1,
    authorized_officer: '',
    contact_info: '',
    submission_date: '',
    signature: '',
    status: STATUS_DRAFT as number,
  });

  // ========== 分頁 ==========
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // ========== 初始化 ==========
  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('NmethProposalPage');
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  // ========== 載入代碼選項 ==========
  useEffect(() => {
    const loadCodes = async () => {
      try {
        const [st, mt, ss, sc] = await Promise.all([
          getSystemCodesByType('A6.4_nmeth_standard_type'),
          getSystemCodesByType('project_type'),
          getSystemCodesByType('Sectoral Scopes'),
          getSystemCodesByType('A6.4_nmeth_status'),
        ]);
        setStandardTypes(st);
        setMitigationTypes(mt);
        setSectoralScopes(ss);
        setStatusCodes(sc);
      } catch (err) {
        console.error('載入代碼選項失敗:', err);
      }
    };
    loadCodes();
  }, []);

  // ========== 載入清單 ==========
  const loadList = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await nmethProposalService.getList();
      setDataList(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || (isZh ? '載入資料失敗' : 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadList(); }, []);

  // ========== 代碼名稱 ==========
  const getCodeName = (codes: SystemCode[], id: number | null): string => {
    if (!id) return '-';
    const code = codes.find(c => c.id === id);
    if (!code) return String(id);
    return isZh ? (code.code_cname || code.code_ename || String(id)) : (code.code_ename || code.code_cname || String(id));
  };

  // ========== 精靈操作 ==========
  const openWizard = (item?: NmethProposalDetail) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        title: item.title || '',
        type_of_standard: item.type_of_standard,
        mitigation_type: item.mitigation_type,
        sectoral_scopes: item.sectoral_scopes || [],
        project_title: item.project_title || '',
        attachment_checks: item.attachment_checks || { ...EMPTY_ATTACHMENTS },
        proponent_name: item.proponent_name || '',
        affiliation: item.affiliation || 1,
        authorized_officer: item.authorized_officer || '',
        contact_info: item.contact_info || '',
        submission_date: item.submission_date || '',
        signature: item.signature || '',
        status: item.status || STATUS_DRAFT,
      });
    } else {
      setEditingItem(null);
      setFormData({
        title: '', type_of_standard: null, mitigation_type: null,
        sectoral_scopes: [], project_title: '',
        attachment_checks: { ...EMPTY_ATTACHMENTS },
        proponent_name: '', affiliation: 1, authorized_officer: '',
        contact_info: '', submission_date: '', signature: '',
        status: STATUS_DRAFT,
      });
    }
    setWizardStep(0);
    setMode('wizard');
  };

  const closeWizard = () => {
    setMode('list');
    setEditingItem(null);
    loadList();
  };

  // ========== Step 驗證 ==========
  const validateStep = (step: number): string | null => {
    if (step === 0) {
      if (!formData.title.trim()) return isZh ? '請填寫方法論標題' : 'Please enter methodology title';
    }
    if (step === 1) {
      if (!formData.proponent_name.trim()) return isZh ? '請填寫提案者名稱' : 'Please enter proponent name';
    }
    return null;
  };

  const goNext = () => {
    const err = validateStep(wizardStep);
    if (err) { toast.warning(err); return; }
    setWizardStep(wizardStep + 1);
  };

  const goPrev = () => {
    setWizardStep(wizardStep - 1);
  };

  // ========== 最終儲存（Step 3） ==========
  const handleFinalSave = async () => {
    try {
      const { status: selectedStatus, ...fields } = formData;
      if (editingItem) {
        const updateData: NmethProposalUpdate & { status?: number } = { ...fields };
        // 先更新欄位
        await nmethProposalService.update(editingItem.id, updateData);
        // 如果狀態有變更且要提交
        if (selectedStatus === STATUS_SUBMITTED && editingItem.status === STATUS_DRAFT) {
          try {
            await nmethProposalService.submit(editingItem.id);
          } catch (err: any) {
            toast.error(err.response?.data?.detail || (isZh ? '提交失敗，資料已儲存為草稿' : 'Submit failed, saved as draft'));
            closeWizard();
            return;
          }
        }
        toast.success(isZh ? '儲存成功' : 'Saved successfully');
      } else {
        const createData: NmethProposalCreate = {
          ...fields,
          type_of_standard: fields.type_of_standard || undefined,
          mitigation_type: fields.mitigation_type || undefined,
        };
        const created = await nmethProposalService.create(createData);
        // 新增後如果選擇提交
        if (selectedStatus === STATUS_SUBMITTED) {
          try {
            await nmethProposalService.submit(created.id);
          } catch (err: any) {
            toast.error(err.response?.data?.detail || (isZh ? '提交失敗，資料已儲存為草稿' : 'Submit failed, saved as draft'));
            closeWizard();
            return;
          }
        }
        toast.success(isZh ? '新增成功' : 'Created successfully');
      }
      closeWizard();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || (isZh ? '儲存失敗' : 'Save failed'));
    }
  };

  // ========== 清單操作 ==========
  const handleDelete = async (item: NmethProposalDetail) => {
    const ok = await toast.confirm({
      title: isZh ? '刪除確認' : 'Delete Confirmation',
      message: isZh ? '確定要刪除此提案嗎？' : 'Are you sure you want to delete this proposal?',
      confirmText: isZh ? '刪除' : 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    try {
      await nmethProposalService.delete(item.id);
      toast.success(isZh ? '刪除成功' : 'Deleted successfully');
      loadList();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || (isZh ? '刪除失敗' : 'Delete failed'));
    }
  };

  // ========== 分頁 ==========
  const totalPages = Math.ceil(dataList.length / itemsPerPage);
  const currentItems = dataList.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // ========== 權限 ==========
  if (permissionLoading) {
    return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  }
  if (!hasPermission('nmeth_proposal', 'read')) {
    return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;
  }
  const canCreate = hasPermission('nmeth_proposal', 'create');
  const canUpdate = hasPermission('nmeth_proposal', 'update');
  const canDelete = hasPermission('nmeth_proposal', 'delete');

  // ========== 共用樣式 ==========
  const fieldStyle: React.CSSProperties = { marginBottom: '16px' };
  const labelStyle: React.CSSProperties = { display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '14px' };
  const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };

  // ================================================================
  // 清單模式
  // ================================================================
  if (mode === 'list') {
    return (
      <div className="page-container">
        <div className="page-header">
          <FunctionPageHeader funcCode="nmeth_proposal" />
        </div>

        {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

        {canCreate && (
          <div style={{ marginBottom: '12px', textAlign: 'right' }}>
            <button className="btn-primary" onClick={() => openWizard()}>
              {t('common.create')}
            </button>
          </div>
        )}

        {loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : dataList.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
            {isZh ? '尚無提案資料，請點擊「新增」按鈕' : 'No proposals yet. Click "Create" to add one.'}
          </div>
        ) : (
          <>
            <div className="data-table-container">
              <table className="data-table">
                <thead className="table-header-dark-green">
                  <tr>
                    <th style={{ width: '5%' }}>#</th>
                    <th style={{ width: '30%' }}>{isZh ? '方法論標題' : 'Methodology Title'}</th>
                    <th style={{ width: '15%' }}>{isZh ? '減緩類型' : 'Mitigation Type'}</th>
                    <th style={{ width: '10%' }}>{isZh ? '狀態' : 'Status'}</th>
                    <th style={{ width: '10%' }}>{isZh ? '完成度' : 'Progress'}</th>
                    <th style={{ width: '15%' }}>{isZh ? '提交日期' : 'Submission Date'}</th>
                    <th style={{ width: '15%' }}>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((item, idx) => {
                    const st = STATUS_LABELS[item.status] || STATUS_LABELS[STATUS_DRAFT];
                    return (
                      <tr key={item.id}>
                        <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                        <td dangerouslySetInnerHTML={{ __html: item.title || '-' }} />
                        <td>{getCodeName(mitigationTypes, item.mitigation_type)}</td>
                        <td>
                          <span style={{
                            padding: '2px 10px', borderRadius: '12px', fontSize: '12px',
                            background: st.color + '18', color: st.color, fontWeight: 600,
                          }}>
                            {isZh ? st.zh : st.en}
                          </span>
                        </td>
                        <td>{item.meth_proposal_pct}%</td>
                        <td>{item.submission_date || '-'}</td>
                        <td className="actions">
                          {canUpdate && item.status === STATUS_DRAFT && (
                            <button className="btn-edit" onClick={() => openWizard(item)} style={{ marginRight: '8px' }}>
                              {t('common.edit')}
                            </button>
                          )}
                          {canDelete && item.status === STATUS_DRAFT && (
                            <button className="btn-delete" onClick={() => handleDelete(item)}>
                              {t('common.delete')}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {dataList.length > itemsPerPage && (
              <div className="pagination-container">
                <div className="pagination-info">
                  <label>
                    {isZh ? '每頁顯示：' : 'Items per page: '}
                    <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(parseInt(e.target.value)); setCurrentPage(1); }}>
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                    </select>
                    {isZh ? '筆' : ''}
                  </label>
                  <span className="pagination-text">
                    {isZh ? `共 ${dataList.length} 筆，第 ${currentPage} / ${totalPages} 頁` : `Total ${dataList.length}, Page ${currentPage} / ${totalPages}`}
                  </span>
                </div>
                <div className="pagination-buttons">
                  <button className="btn-pagination" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>⟪</button>
                  <button className="btn-pagination" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>‹</button>
                  <button className="btn-pagination" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>›</button>
                  <button className="btn-pagination" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>⟫</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ================================================================
  // 精靈模式
  // ================================================================
  const canEdit = editingItem ? editingItem.status === STATUS_DRAFT && canUpdate : canCreate;

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>
          {editingItem ? (isZh ? '編輯方法論提案' : 'Edit Methodology Proposal') : (isZh ? '新增方法論提案' : 'New Methodology Proposal')}
        </h2>
        <button className="btn-secondary" onClick={closeWizard}>{isZh ? '返回清單' : 'Back to List'}</button>
      </div>

      {/* 步驟指示器 */}
      <div style={{ display: 'flex', alignItems: 'center', margin: '20px 0 24px', gap: '0' }}>
        {WIZARD_STEPS.map((step, idx) => (
          <React.Fragment key={idx}>
            <div
              onClick={() => { if (idx < wizardStep) setWizardStep(idx); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                cursor: idx < wizardStep ? 'pointer' : 'default',
                opacity: idx <= wizardStep ? 1 : 0.4,
              }}
            >
              <div style={{
                width: '32px', height: '32px', borderRadius: '50%', display: 'flex',
                alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 700,
                background: idx === wizardStep ? '#4A90E2' : idx < wizardStep ? '#52c41a' : '#e0e0e0',
                color: '#fff',
              }}>
                {idx < wizardStep ? '✓' : idx + 1}
              </div>
              <span style={{
                fontSize: '13px', fontWeight: idx === wizardStep ? 700 : 400,
                color: idx === wizardStep ? '#4A90E2' : '#666',
              }}>
                {isZh ? step.zh : step.en}
              </span>
            </div>
            {idx < WIZARD_STEPS.length - 1 && (
              <div style={{ flex: 1, height: '2px', background: idx < wizardStep ? '#52c41a' : '#e0e0e0', margin: '0 12px' }} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: 方法論基本資訊 */}
      {wizardStep === 0 && (
        <div style={{ width: '100%' }}>
          <div style={fieldStyle}>
            <label style={labelStyle}>{isZh ? '標準類型' : 'Type of Standard'}</label>
            <select style={inputStyle} value={formData.type_of_standard || ''} disabled={!canEdit}
              onChange={(e) => setFormData({ ...formData, type_of_standard: e.target.value ? Number(e.target.value) : null })}>
              <option value="">{isZh ? '請選擇' : 'Select...'}</option>
              {standardTypes.map(s => (
                <option key={s.id} value={s.id}>{isZh ? (s.code_cname || s.code_ename) : (s.code_ename || s.code_cname)}</option>
              ))}
            </select>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>{isZh ? '方法論標題' : 'Title of Methodology'} *</label>
            <RichTextEditor
              value={formData.title}
              onChange={(html) => setFormData({ ...formData, title: html })}
              disabled={!canEdit}
              placeholder={isZh ? '請輸入方法論或方法學工具名稱' : 'Enter methodology or tool name'}
              minHeight="40px"
              toolbar="inline"
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>{isZh ? '減緩活動類型' : 'Type of Mitigation Activity'}</label>
            <select style={inputStyle} value={formData.mitigation_type || ''} disabled={!canEdit}
              onChange={(e) => setFormData({ ...formData, mitigation_type: e.target.value ? Number(e.target.value) : null })}>
              <option value="">{isZh ? '請選擇' : 'Select...'}</option>
              {mitigationTypes.map(m => (
                <option key={m.id} value={m.id}>{isZh ? (m.code_cname || m.code_ename) : (m.code_ename || m.code_cname)}</option>
              ))}
            </select>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>{isZh ? '適用部門範疇' : 'Sectoral Scope(s)'}</label>
            <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '12px', maxHeight: '240px', overflowY: 'auto' }}>
              {sectoralScopes.map(s => (
                <label key={s.id} style={{ display: 'block', marginBottom: '6px', fontSize: '13px', cursor: canEdit ? 'pointer' : 'default' }}>
                  <input type="checkbox" disabled={!canEdit}
                    checked={formData.sectoral_scopes.includes(s.id)}
                    onChange={(e) => {
                      const scopes = e.target.checked
                        ? [...formData.sectoral_scopes, s.id]
                        : formData.sectoral_scopes.filter(id => id !== s.id);
                      setFormData({ ...formData, sectoral_scopes: scopes });
                    }}
                    style={{ marginRight: '8px' }} />
                  {isZh ? (s.code_cname || s.code_ename) : (s.code_ename || s.code_cname)}
                </label>
              ))}
            </div>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>{isZh ? '擬適用專案/PoA 標題' : 'Title of Proposed A6.4 Project or PoA'}</label>
            <RichTextEditor
              value={formData.project_title}
              onChange={(html) => setFormData({ ...formData, project_title: html })}
              disabled={!canEdit}
              placeholder={isZh ? '請輸入專案或 PoA 名稱' : 'Enter project or PoA title'}
              minHeight="40px"
              toolbar="inline"
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>{isZh ? '附件' : 'Attachments'}</label>
            <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '12px' }}>
              {[
                { key: 'draft_pdd' as const, label: 'Draft PDD' },
                { key: 'draft_poa_dd' as const, label: 'Draft PoA-DD' },
                { key: 'meth_form_002' as const, label: 'A6.4-FORM-METH-002 *' },
                { key: 'additional_info' as const, label: isZh ? '其他支援文件' : 'Additional supporting information' },
              ].map(att => (
                <label key={att.key} style={{ display: 'block', marginBottom: '6px', fontSize: '13px', cursor: canEdit ? 'pointer' : 'default' }}>
                  <input type="checkbox" disabled={!canEdit}
                    checked={formData.attachment_checks[att.key]}
                    onChange={(e) => setFormData({
                      ...formData,
                      attachment_checks: { ...formData.attachment_checks, [att.key]: e.target.checked }
                    })}
                    style={{ marginRight: '8px' }} />
                  {att.label}
                </label>
              ))}
              {formData.attachment_checks.additional_info && (
                <input style={{ ...inputStyle, marginTop: '8px' }} value={formData.attachment_checks.additional_description} disabled={!canEdit}
                  onChange={(e) => setFormData({
                    ...formData,
                    attachment_checks: { ...formData.attachment_checks, additional_description: e.target.value }
                  })}
                  placeholder={isZh ? '請說明附加文件內容' : 'Specify additional information'} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step 2: 提案者資訊 */}
      {wizardStep === 1 && (
        <div style={{ width: '100%' }}>
          <div style={fieldStyle}>
            <label style={labelStyle}>{isZh ? '提案者名稱' : 'Name of the Proponent'} *</label>
            <input style={inputStyle} value={formData.proponent_name} disabled={!canEdit}
              onChange={(e) => setFormData({ ...formData, proponent_name: e.target.value })} />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>{isZh ? '所屬類別' : 'Affiliation'}</label>
            {[
              { val: 1, label: isZh ? '計畫專案活動參與者或 PoA 協調/管理實體' : 'Activity participant of a planned project or coordinating/managing entity of a planned PoA' },
              { val: 2, label: isZh ? '指定國家主管機關 (DNA)' : 'Designated National Authority (DNA)' },
              { val: 3, label: isZh ? '指定作業實體 (DOE)' : 'Designated Operational Entity (DOE)' },
              { val: 4, label: isZh ? '其他利害關係人' : 'Other stakeholder' },
            ].map(opt => (
              <label key={opt.val} style={{ display: 'block', marginBottom: '8px', fontSize: '13px', cursor: canEdit ? 'pointer' : 'default' }}>
                <input type="radio" name="affiliation" value={opt.val} disabled={!canEdit}
                  checked={formData.affiliation === opt.val}
                  onChange={() => setFormData({ ...formData, affiliation: opt.val })}
                  style={{ marginRight: '8px' }} />
                {opt.label}
              </label>
            ))}
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>{isZh ? '授權簽署人姓名' : 'Name of Authorized Officer'}</label>
            <input style={inputStyle} value={formData.authorized_officer} disabled={!canEdit}
              onChange={(e) => setFormData({ ...formData, authorized_officer: e.target.value })} />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>{isZh ? '聯絡資訊' : 'Contact Information'}</label>
            <textarea style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} value={formData.contact_info} disabled={!canEdit}
              onChange={(e) => setFormData({ ...formData, contact_info: e.target.value })}
              placeholder={isZh ? 'E-mail 及電話' : 'E-mail addresses and phone contacts'} />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>{isZh ? '提交日期' : 'Submission Date'}</label>
            <DateInput value={formData.submission_date} disabled={!canEdit}
              onChange={(v) => setFormData({ ...formData, submission_date: v })} />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>{isZh ? '簽署' : 'Signature'}</label>
            <input style={inputStyle} value={formData.signature} disabled={!canEdit}
              onChange={(e) => setFormData({ ...formData, signature: e.target.value })} />
          </div>
        </div>
      )}

      {/* Step 3: 狀態確認與儲存 */}
      {wizardStep === 2 && (
        <div style={{ width: '100%' }}>
          {/* 資料摘要 */}
          <div style={{ marginBottom: '24px', padding: '16px 20px', border: '1px solid #e8e8e8', borderRadius: '8px', background: '#fafafa' }}>
            <div style={{ fontSize: '15px', fontWeight: 700, marginBottom: '12px' }}>
              {isZh ? '資料摘要' : 'Summary'}
            </div>
            <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
              <tbody>
                <tr><td style={{ padding: '4px 0', color: '#666', width: '30%' }}>{isZh ? '方法論標題' : 'Title'}</td><td style={{ fontWeight: 600 }} dangerouslySetInnerHTML={{ __html: formData.title || '-' }} /></tr>
                <tr><td style={{ padding: '4px 0', color: '#666' }}>{isZh ? '減緩活動類型' : 'Mitigation Type'}</td><td>{getCodeName(mitigationTypes, formData.mitigation_type)}</td></tr>
                <tr><td style={{ padding: '4px 0', color: '#666' }}>{isZh ? '適用部門範疇' : 'Sectoral Scopes'}</td><td>{formData.sectoral_scopes.length > 0 ? formData.sectoral_scopes.map(id => getCodeName(sectoralScopes, id)).join(', ') : '-'}</td></tr>
                <tr><td style={{ padding: '4px 0', color: '#666' }}>{isZh ? '提案者名稱' : 'Proponent'}</td><td>{formData.proponent_name || '-'}</td></tr>
                <tr><td style={{ padding: '4px 0', color: '#666' }}>{isZh ? '聯絡資訊' : 'Contact'}</td><td>{formData.contact_info || '-'}</td></tr>
                <tr><td style={{ padding: '4px 0', color: '#666' }}>{isZh ? '提交日期' : 'Date'}</td><td>{formData.submission_date || '-'}</td></tr>
                <tr><td style={{ padding: '4px 0', color: '#666' }}>{isZh ? 'METH-002 已附' : 'METH-002 attached'}</td><td>{formData.attachment_checks.meth_form_002 ? '✓' : '✗'}</td></tr>
              </tbody>
            </table>
          </div>

          {/* 狀態設定 */}
          <div style={fieldStyle}>
            <label style={labelStyle}>{isZh ? '設定提案狀態' : 'Set Proposal Status'}</label>
            <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '12px' }}>
              {statusCodes.map(sc => {
                const st = STATUS_LABELS[sc.id];
                return (
                  <label key={sc.id} style={{ display: 'flex', alignItems: 'center', marginBottom: '10px', fontSize: '14px', cursor: canEdit ? 'pointer' : 'default' }}>
                    <input type="radio" name="status" value={sc.id} disabled={!canEdit}
                      checked={formData.status === sc.id}
                      onChange={() => setFormData({ ...formData, status: sc.id })}
                      style={{ marginRight: '10px', width: '18px', height: '18px' }} />
                    <span style={{
                      display: 'inline-block', padding: '2px 12px', borderRadius: '12px', fontSize: '13px', fontWeight: 600,
                      background: st ? st.color + '18' : '#f0f0f0', color: st ? st.color : '#333',
                    }}>
                      {isZh ? (sc.code_cname || sc.code_ename) : (sc.code_ename || sc.code_cname)}
                    </span>
                  </label>
                );
              })}
            </div>
            {formData.status === STATUS_SUBMITTED && (
              <div style={{ marginTop: '8px', padding: '8px 12px', background: '#fff7e6', border: '1px solid #ffe58f', borderRadius: '6px', fontSize: '12px', color: '#d48806' }}>
                {isZh ? '提交後將鎖定不可編輯，請確認資料無誤。' : 'Once submitted, the proposal will be locked and cannot be edited.'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 步驟導航按鈕 */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '32px', paddingBottom: '24px' }}>
        {wizardStep > 0 && (
          <button className="btn-secondary" onClick={goPrev} style={{ padding: '8px 24px' }}>
            {isZh ? '← 上一步' : '← Previous'}
          </button>
        )}
        {wizardStep < 2 && canEdit && (
          <button className="btn-primary" onClick={goNext} style={{ padding: '8px 24px' }}>
            {isZh ? '下一步 →' : 'Next →'}
          </button>
        )}
        {wizardStep === 2 && canEdit && (
          <button className="btn-primary" onClick={handleFinalSave} style={{ padding: '8px 32px', fontSize: '15px', fontWeight: 700 }}>
            {isZh ? '儲存' : 'Save'}
          </button>
        )}
        <button className="btn-secondary" onClick={closeWizard} style={{ padding: '8px 24px' }}>
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
};

export default NmethProposalPage;
